import {
	type ActivityTypesEnum,
	type FetchSocialFeedMeByFeedSlugParams,
	useLocale,
	useSettings
} from '@capsicum/common';
import { mySociosFeedKeys } from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { suggestedTeamsFeedFiltersAtom, useSocialFeedMeQuery } from '../data-access';
import { type FeedFiltersMapType } from '../models';
import { prepareFeedData } from '../util';

import { useFollowedTeamsFeed } from './useFollowedTeamsFeed';

/**
 * @warning DON'T USE this hook directly! use the one implemented on useSuggestedTasksFeedWEB or useSuggestedTasksFeedMOB files instead.
 */

export const useSuggestedTasksFeed = (filtersMap: FeedFiltersMapType) => {
	const { locale } = useLocale();
	const { data: followedFeedData, hasNextPage: hasFollowedFeedNextPage } = useFollowedTeamsFeed(filtersMap);
	const {
		settings: { isSuggestedTasksFeedEnabled, isSuggestedTasksFeedFiltersEnabled }
	} = useSettings();
	const { partners = [], selectedFilters } = useAtomValue(suggestedTeamsFeedFiltersAtom);
	const key = [...mySociosFeedKeys.suggestedTeams, selectedFilters, ...partners];
	const activityTypes = selectedFilters
		.map((x: any) => filtersMap.get(x) || [])
		.flat() as unknown as Array<ActivityTypesEnum>;

	const params: FetchSocialFeedMeByFeedSlugParams = {
		'activityTypes[]': activityTypes,
		feedSlug: 'suggested_tasks',
		limit: 20,
		locale: locale,
		'partnerIds[]': partners
	};

	const { data, isLoading: isLoadingSuggestedTasks } = useSocialFeedMeQuery(key, params);

	const { feedData, metadata } = prepareFeedData(data);

	const isSuggestedTasksVisible = useMemo(
		() =>
			isSuggestedTasksFeedEnabled &&
			(followedFeedData.length === 0 || (followedFeedData.length > 0 && !hasFollowedFeedNextPage)),
		[feedData, isLoadingSuggestedTasks, followedFeedData, hasFollowedFeedNextPage, isSuggestedTasksFeedEnabled]
	);

	return {
		feedData,
		isLoadingSuggestedTasks,
		isSuggestedTasksFeedFiltersEnabled,
		isSuggestedTasksVisible,
		metadata
	};
};
